import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FmlxButton, FmlxModal, FmlxProgressBar } from 'fmlx-common-ui';
import { RunUnarchiveDTO, RunUnarchiveExperimentDTO } from 'business/models/Models';
import { useRMDispatch } from 'redux/hooks';
import { useSelectUnarchivingBackgroundJob } from 'redux/hooks/backgroundJobs';
import {
  useSelectArchivedTasks,
  useSelectPreviewExperiments,
} from 'redux/hooks/unarchiving';
import {
  fetchArchivedExperimentsThunk,
  runUnarchiveThunk,
} from 'redux/thunks/unarchiving';
import { clearPreviewExperiments } from 'redux/reducers/unarchivingReducer';
import { useModalHandlers } from 'components/modal';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { CustomTable } from '../controls/customTable';
import {
  getArchivingArchivedLabel,
  getArchivingStatusLabel,
} from '../hooks/useArchivingProgressHub';
import { getUnarchivePreviewView, UnarchivePreviewView } from './utils';
import { useIsUnarchivingJobActive } from './hooks';
import { UnarchiveProjectModalBody } from './unarchiveProjectModal';
import {
  UnarchivePreviewColumns,
  UNARCHIVE_PREVIEW_ROWS_PER_PAGE,
  UnarchiveTableProps,
} from './constants';

type SelectionCounts = {
  total: number;
  selected: number;
  selectedExperimentIds: number[];
};

const formatDate = (value?: Date) => value?.toLocaleDateString('en-US');
const EMPTY_COUNTS: SelectionCounts = {
  total: 0,
  selected: 0,
  selectedExperimentIds: [],
};

export const UnarchivePreviewTable = (props: UnarchivePreviewTableProps) => {
  const { selectedTaskIds, onUnarchiveSuccess } = props;
  const dispatch = useRMDispatch();
  const { data: experiments, isLoading } = useSelectPreviewExperiments();
  const { data: archivedTasks } = useSelectArchivedTasks();
  const { showModal, hideModal } = useModalHandlers();
  const isJobActive = useIsUnarchivingJobActive();
  const unarchivingJob = useSelectUnarchivingBackgroundJob();
  const [counts, setCounts] = useState<SelectionCounts>(EMPTY_COUNTS);

  const handleRunUnarchive = useCallback(
    async (projectName: string) => {
      const selectedRows = experiments.filter((row) =>
        counts.selectedExperimentIds.includes(row.experimentId),
      );

      const request = new RunUnarchiveDTO({
        projectName,
        experiments: selectedRows.map(
          (row) =>
            new RunUnarchiveExperimentDTO({
              experimentId: row.experimentId,
              experimentName: row.experimentName,
              originalLocation: row.originalLocation,
              dateCreated: row.dateCreated,
              inspectionDate: row.inspectionDate,
              archivedDate: row.archivedDate,
            }),
        ),
      });

      try {
        const result = await dispatch(runUnarchiveThunk(request)).unwrap();
        dispatch(clearPreviewExperiments());
        onUnarchiveSuccess?.();
        hideModal();
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          result.message || 'Unarchive job queued successfully.',
        );
      } catch (error) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          typeof error === 'string' ? error : 'Failed to start unarchive job.',
        );
      }
    },
    [counts.selectedExperimentIds, dispatch, experiments, hideModal, onUnarchiveSuccess],
  );

  const firstSelectedTaskName = useMemo(() => {
    const firstSelectedTask = archivedTasks.find((task) =>
      selectedTaskIds.includes(task.jobId),
    );
    return firstSelectedTask?.name ?? '';
  }, [archivedTasks, selectedTaskIds]);

  const handleUnarchiveClick = useCallback(() => {
    showModal({
      heading: 'Unarchive Experiments',
      id: 'customWidthModal',
      size: FmlxModal.Size.MEDIUM,
      hideFooter: true,
      onHide: hideModal,
      body: (
        <UnarchiveProjectModalBody
          experimentCount={counts.selected}
          defaultProjectName={firstSelectedTaskName}
          onClose={hideModal}
          onRunUnarchive={handleRunUnarchive}
        />
      ),
    });
  }, [counts.selected, firstSelectedTaskName, handleRunUnarchive, hideModal, showModal]);

  // Fetch the archived experiments for the selected task(s); clear when nothing
  // is selected so the empty state shows.
  useEffect(() => {
    setCounts(EMPTY_COUNTS);
    if (selectedTaskIds.length > 0) {
      dispatch(fetchArchivedExperimentsThunk(selectedTaskIds));
    } else {
      dispatch(clearPreviewExperiments());
    }
  }, [dispatch, selectedTaskIds]);

  const data = useMemo(
    () =>
      experiments.map((row) => ({
        name: row.experimentName,
        fileLocation: row.currentLocation,
        dateCreated: formatDate(row.dateCreated),
        lastInspectionDate: formatDate(row.inspectionDate),
        dateArchived: formatDate(row.archivedDate),
        status: 'Archived',
        __experimentId: row.experimentId,
      })),
    [experiments],
  );

  const previewView = getUnarchivePreviewView({
    isLoading,
    hasSelection: selectedTaskIds.length > 0,
    rowCount: data.length,
    isJobActive,
  });

  const restoredLabel = getArchivingArchivedLabel(unarchivingJob);
  const restoredLabelText = restoredLabel ? 'Restored:' : 'Unarchive:';
  const statusLabel = getArchivingStatusLabel(unarchivingJob);

  return (
    <div className="tab-content-height w-full flex flex-col">
      <div className="basis-[95%] h-[90%] w-full">
        <UnarchivePreviewContent
          view={previewView}
          data={data}
          onCountsChange={setCounts}
        />
      </div>
      <div className="basis-[5%] w-full flex items-center border-t border-b border-gray-300 bg-white px-4 py-2 font-sans text-xs text-gray-700 select-none">
        <div>
          <span className="text-gray-500">Selected:</span>{' '}
          <span className="font-semibold text-gray-900">
            {counts.selected}/{counts.total}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-6">
          <div>
            <span className="text-gray-500">{restoredLabelText}</span>{' '}
            <span className="font-semibold text-gray-900">{restoredLabel}</span>
          </div>
          <div>
            <span className="text-gray-500">Status:</span>{' '}
            <span className="font-semibold text-gray-900">{statusLabel}</span>
          </div>
          <FmlxButton
            disabled={counts.selected === 0 || isJobActive}
            onClick={handleUnarchiveClick}
            size="sm"
            type={FmlxButton.Type.POSITIVE}
            label="Unarchive"
          />
        </div>
      </div>
    </div>
  );
};

type UnarchivePreviewTableProps = UnarchiveTableProps;

const UnarchivePreviewLoading = () => (
  <div
    data-testid="unarchive-preview-loading"
    className="flex h-full flex-col items-center justify-center gap-4"
  >
    <FmlxProgressBar type="circular" variant={FmlxProgressBar.Variant.INDETERMINATE} />
    <span className="font-sans text-base text-gray-600">Loading experiments ...</span>
  </div>
);

const UnarchivePreviewEmpty = () => (
  <div
    data-testid="unarchive-preview-empty"
    className="flex flex-col h-full items-center justify-center text-xl text-[#737373]"
  >
    <span>No data shown</span>
    <span>Select an archived task to preview its experiments</span>
  </div>
);

type UnarchivePreviewContentProps = {
  view: UnarchivePreviewView;
  data: Array<Record<string, unknown>>;
  onCountsChange: (counts: SelectionCounts) => void;
};

const UnarchivePreviewContent = ({
  view,
  data,
  onCountsChange,
}: UnarchivePreviewContentProps) => {
  switch (view) {
    case 'loading':
      return <UnarchivePreviewLoading />;
    case 'empty':
      return <UnarchivePreviewEmpty />;
    case 'table':
      return (
        <CustomTable
          data={data}
          columns={UnarchivePreviewColumns}
          id="unarchive-preview-table"
          autoSelectAll
          onCountsChange={onCountsChange}
          sortInfo={{ direction: 'asc', column: 'dateArchived' }}
          rowsPerPage={UNARCHIVE_PREVIEW_ROWS_PER_PAGE}
        />
      );
  }
};
